import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MagicSelectTrigger as SelectTrigger } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/hooks/useAuth'
import {
  storage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from '@/lib/firebase/storage'
import { createUserFacingError, formatUserFacingError, withErrorCode } from '@/lib/errors'
import type { TaxEntityType, MemberTaxProfileDocument } from '@/types'

interface AddressInput {
  line1: string
  line2?: string
  city: string
  state: string
  postalCode: string
}

interface W9WizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingProfile: (MemberTaxProfileDocument & { id: string }) | null
  onSubmit: (input: {
    legalName: string
    businessName?: string
    address: AddressInput
    entityType: TaxEntityType
    backupWithholding: boolean
    signatureImageUrl: string
    taxIdPlain: string
  }) => Promise<void>
}

const ENTITY_OPTIONS: { value: TaxEntityType; label: string }[] = [
  { value: 'individual', label: 'Individual / Sole Proprietor' },
  { value: 'sole_prop', label: 'Sole Proprietor (DBA)' },
  { value: 'llc_single', label: 'Single-Member LLC' },
  { value: 'llc_multi', label: 'Multi-Member LLC' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
]

function maskTaxId(digits: string): string {
  const d = digits.replace(/\D/g, '')
  if (d.length < 4) return '***-**-****'
  return `***-**-${d.slice(-4)}`
}

function formatTaxIdInput(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 9)
  if (d.length <= 3) return d
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`
}

export function W9Wizard({ open, onOpenChange, existingProfile, onSubmit }: W9WizardProps) {
  const { user } = useAuth()
  const [step, setStep] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [uploadingSig, setUploadingSig] = useState(false)

  const [legalName, setLegalName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [address, setAddress] = useState<AddressInput>({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
  })
  const [entityType, setEntityType] = useState<TaxEntityType>('individual')
  const [taxIdRaw, setTaxIdRaw] = useState('')
  const [backupWithholding, setBackupWithholding] = useState(false)
  const [signatureText, setSignatureText] = useState('')
  const [signatureImageUrl, setSignatureImageUrl] = useState('')

  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (!open) return
    setStep(0)
    setError(null)
    setSubmitting(false)
    setTaxIdRaw('')
    if (existingProfile) {
      setLegalName(existingProfile.legalName ?? '')
      setBusinessName(existingProfile.businessName ?? '')
      setAddress({
        line1: existingProfile.address?.line1 ?? '',
        line2: existingProfile.address?.line2 ?? '',
        city: existingProfile.address?.city ?? '',
        state: existingProfile.address?.state ?? '',
        postalCode: existingProfile.address?.postalCode ?? '',
      })
      setEntityType(existingProfile.entityType ?? 'individual')
      setBackupWithholding(existingProfile.backupWithholding ?? false)
      setSignatureImageUrl(existingProfile.signatureImageUrl ?? '')
      setSignatureText(existingProfile.legalName ?? '')
    } else {
      setLegalName('')
      setBusinessName('')
      setAddress({ line1: '', line2: '', city: '', state: '', postalCode: '' })
      setEntityType('individual')
      setBackupWithholding(false)
      setSignatureImageUrl('')
      setSignatureText('')
    }
  }, [open, existingProfile])

  useEffect(() => {
    if (step !== 5) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#111111'
    ctx.font = 'italic 44px "Brush Script MT", "Segoe Script", cursive'
    ctx.textBaseline = 'middle'
    ctx.fillText(signatureText || '', 16, canvas.height / 2)
  }, [signatureText, step])

  const taxIdDigits = taxIdRaw.replace(/\D/g, '')

  const stepValid = (() => {
    switch (step) {
      case 0:
        return legalName.trim().length > 0
      case 1:
        return (
          address.line1.trim().length > 0 &&
          address.city.trim().length > 0 &&
          /^[A-Za-z]{2}$/.test(address.state.trim()) &&
          address.postalCode.trim().length >= 5
        )
      case 2:
        return !!entityType
      case 3:
        return taxIdDigits.length === 9
      case 4:
        return true
      case 5:
        return signatureText.trim().length > 0 && !!signatureImageUrl && !uploadingSig
      case 6:
        return true
      default:
        return false
    }
  })()

  async function captureSignature() {
    if (!user) {
      setError(withErrorCode('Not signed in.', 'app/validation'))
      return
    }
    const canvas = canvasRef.current
    if (!canvas) return
    setError(null)
    setUploadingSig(true)
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png'),
      )
      if (!blob) throw createUserFacingError('We could not capture your signature.')
      const path = `taxSignatures/${user.uid}/${Date.now()}.png`
      const ref = storageRef(storage, path)
      const task = uploadBytesResumable(ref, blob, { contentType: 'image/png' })
      await new Promise<void>((resolve, reject) => {
        task.on('state_changed', undefined, reject, () => resolve())
      })
      const url = await getDownloadURL(ref)
      setSignatureImageUrl(url)
    } catch (e) {
      setError(formatUserFacingError(e, 'Failed to upload signature.'))
    } finally {
      setUploadingSig(false)
    }
  }

  async function handleFinalSubmit() {
    setError(null)
    setSubmitting(true)
    try {
      await onSubmit({
        legalName: legalName.trim(),
        businessName: businessName.trim() || undefined,
        address: {
          line1: address.line1.trim(),
          line2: address.line2?.trim() || undefined,
          city: address.city.trim(),
          state: address.state.trim().toUpperCase(),
          postalCode: address.postalCode.trim(),
        },
        entityType,
        backupWithholding,
        signatureImageUrl,
        taxIdPlain: taxIdDigits,
      })
    } catch (e) {
      setError(formatUserFacingError(e, 'Failed to submit W-9.'))
    } finally {
      setSubmitting(false)
    }
  }

  const STEP_TITLES = [
    'Identity',
    'Address',
    'Entity Type',
    'Tax ID',
    'Backup Withholding',
    'Signature',
    'Review',
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>W-9: {STEP_TITLES[step]}</DialogTitle>
          <p className="text-xs text-muted-foreground">Step {step + 1} of {STEP_TITLES.length}</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {step === 0 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="legalName">Legal name *</Label>
                <Input id="legalName" value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="As shown on your tax return" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessName">Business / DBA name (optional)</Label>
                <Input id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="line1">Street address *</Label>
                <Input id="line1" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="line2">Apt / Suite (optional)</Label>
                <Input id="line2" value={address.line2 ?? ''} onChange={(e) => setAddress({ ...address, line2: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input id="city" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input id="state" maxLength={2} value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase() })} placeholder="CA" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">ZIP *</Label>
                <Input id="postalCode" value={address.postalCode} onChange={(e) => setAddress({ ...address, postalCode: e.target.value })} />
              </div>
            </>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <Label>Federal tax classification *</Label>
              <Select value={entityType} onValueChange={(v) => setEntityType(v as TaxEntityType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2">
              <Label htmlFor="taxId">Tax ID (SSN or EIN) *</Label>
              <Input
                id="taxId"
                value={formatTaxIdInput(taxIdRaw)}
                onChange={(e) => setTaxIdRaw(e.target.value)}
                placeholder="XXX-XX-XXXX"
                inputMode="numeric"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Your SSN or EIN. Stored encrypted on our server.
                {existingProfile?.hasTaxIdOnFile && ' Re-enter your tax ID to update.'}
              </p>
              {taxIdRaw.length > 0 && taxIdDigits.length !== 9 && (
                <p className="text-xs text-destructive">
                  {withErrorCode('Please enter all 9 digits.', 'app/validation')}
                </p>
              )}
            </div>
          )}

          {step === 4 && (
            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <Checkbox checked={backupWithholding} onCheckedChange={(v) => setBackupWithholding(!!v)} />
              <span>I am subject to backup withholding (most providers leave unchecked).</span>
            </label>
          )}

          {step === 5 && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="sigText">Type your legal name to sign *</Label>
                <Input id="sigText" value={signatureText} onChange={(e) => { setSignatureText(e.target.value); setSignatureImageUrl('') }} />
              </div>
              <div className="rounded-md border bg-white p-2">
                <canvas ref={canvasRef} width={460} height={90} className="w-full" />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={signatureImageUrl ? 'outline' : 'default'}
                  size="sm"
                  disabled={!signatureText.trim() || uploadingSig}
                  onClick={() => void captureSignature()}
                >
                  {uploadingSig ? 'Uploading…' : signatureImageUrl ? 'Re-capture signature' : 'Capture signature'}
                </Button>
                {signatureImageUrl && (
                  <span className="text-xs text-emerald-600">Signature captured ✓</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                By signing, you declare under penalty of perjury that the information provided is correct.
              </p>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-3 text-sm">
              <ReviewRow label="Legal name" value={legalName} />
              {businessName && <ReviewRow label="Business" value={businessName} />}
              <ReviewRow
                label="Address"
                value={`${address.line1}${address.line2 ? `, ${address.line2}` : ''}, ${address.city}, ${address.state} ${address.postalCode}`}
              />
              <ReviewRow label="Entity" value={ENTITY_OPTIONS.find((o) => o.value === entityType)?.label ?? entityType} />
              <ReviewRow label="Tax ID" value={maskTaxId(taxIdDigits)} />
              <ReviewRow label="Backup withholding" value={backupWithholding ? 'Yes' : 'No'} />
              <ReviewRow label="Signature" value={signatureText} />
              <Separator />
              <p className="text-xs text-muted-foreground">
                Submitting will encrypt your tax ID and create your W-9 on file.
              </p>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="flex sm:justify-between gap-2">
          <Button variant="ghost" disabled={step === 0 || submitting} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            Back
          </Button>
          {step < STEP_TITLES.length - 1 ? (
            <Button disabled={!stepValid} onClick={() => setStep((s) => s + 1)}>Next</Button>
          ) : (
            <Button disabled={submitting || !signatureImageUrl || taxIdDigits.length !== 9} onClick={() => void handleFinalSubmit()}>
              {submitting ? 'Submitting…' : 'Submit W-9'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  )
}
